import { useCallback, useMemo } from 'react';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { NULL_FUNCTION } from '@e-socna/empty';
import { normalizeOrganizationIdentifierValue } from '@e-socna/third-parties';

import { Dialog } from '../../components/Dialog';

import { EstablishmentsResultsList } from './EstablishmentsResultsList';
import { EstablishmentsSelectionGrid } from './EstablishmentsSelectionGrid';
import { useExistingOrganizationIdentifiers } from './hooks/useExistingOrganizationIdentifiers.hook';
import { useSelectAllRowSelectionModel } from './hooks/useSelectAllRowSelectionModel.hook';
import type { ArmedEstablishment } from './EstablishmentsResultsList';
import type { ActiveMembership } from './types/active-membership.type';
import type { EstablishmentListItem } from './utils/build-establishments-list.util';
import type { OrganizationSearchResult } from './types/organization-search-result.type';

interface Props {
  readonly activeMemberships: readonly ActiveMembership[];
  readonly armedEstablishments: readonly ArmedEstablishment[] | null;
  readonly establishments: readonly EstablishmentListItem[];
  readonly hasPendingRows: boolean;
  readonly isOpen: boolean;
  readonly onCancel: () => void;
  readonly onPendingChange: (hasPending: boolean) => void;
  readonly onStartCreation: (
    selectedEstablishments: readonly EstablishmentListItem[],
  ) => void;
  readonly result: OrganizationSearchResult | null;
}

export const EstablishmentsConfirmDialog = ({
  activeMemberships,
  armedEstablishments,
  establishments,
  hasPendingRows,
  isOpen,
  onCancel,
  onPendingChange,
  onStartCreation,
  result,
}: Readonly<Props>) => {
  const existingOrganizationIdentifiers = useExistingOrganizationIdentifiers();

  const alreadyCreatedIndices = useMemo(
    () =>
      new Set(
        establishments
          .map(({ establishment }, index) =>
            existingOrganizationIdentifiers.has(
              normalizeOrganizationIdentifierValue(establishment.siret),
            )
              ? index
              : null,
          )
          .filter((index): index is number => index !== null),
      ),
    [establishments, existingOrganizationIdentifiers],
  );

  const {
    resolveSelectedIndices,
    rowSelectionModel,
    selectedCount,
    setRowSelectionModel,
  } = useSelectAllRowSelectionModel({
    excludedIndices: alreadyCreatedIndices,
    items: establishments,
  });

  const handleConfirmSelection = useCallback(() => {
    const selectedIds = resolveSelectedIndices();

    onStartCreation(
      establishments.filter(
        (_establishment, index) =>
          selectedIds.has(index) && !alreadyCreatedIndices.has(index),
      ),
    );
  }, [
    alreadyCreatedIndices,
    establishments,
    onStartCreation,
    resolveSelectedIndices,
  ]);

  const isResultsPhase = armedEstablishments !== null;

  const content =
    isResultsPhase && result ? (
      <EstablishmentsResultsList
        activeMemberships={activeMemberships}
        armedEstablishments={armedEstablishments}
        onPendingChange={onPendingChange}
        result={result}
      />
    ) : (
      <Stack spacing={1}>
        <Typography variant="body2">
          Créer une Organisation pour chacun de ces établissements ? Le siège,
          si sélectionné, devient l&apos;organisation mère des autres
          établissements sélectionnés. Au moins un établissement doit être
          sélectionné.
        </Typography>
        <EstablishmentsSelectionGrid
          alreadyCreatedIndices={alreadyCreatedIndices}
          establishments={establishments}
          onRowSelectionModelChange={setRowSelectionModel}
          rowSelectionModel={rowSelectionModel}
        />
      </Stack>
    );

  return (
    <Dialog
      confirmLabel={
        isResultsPhase ? 'Fermer' : 'Créer les organisations sélectionnées'
      }
      content={content}
      id="organization-search-establishments-confirm-dialog"
      isConfirmDisabled={isResultsPhase ? hasPendingRows : selectedCount === 0}
      isVisible={isOpen}
      onCancel={onCancel}
      onConfirm={isResultsPhase ? onCancel : handleConfirmSelection}
      setIsVisible={NULL_FUNCTION}
      showCancel={!isResultsPhase}
      title="Établissements à créer"
    />
  );
};
